import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Checkbox,
  FormControlLabel,
  Slider,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";

export type VarianceTab = "noChange" | "withChange" | "all";

export interface VarianceFilterState {
  activeTab: VarianceTab;
  inverted: boolean;
  sliderRange: [number, number];
}

interface VarianceFilterProps {
  /** All unfiltered rows from the grid */
  rows: any[];
  /** Field name that holds the numeric variance value */
  numericField: string;
  /** Callback with the filtered rows whenever tab or slider changes */
  onFilteredRows: (filtered: any[]) => void;
  /** Action buttons (Take Action, Download) rendered to the right */
  actions?: React.ReactNode;
  /** Column header label displayed above the slider */
  columnLabel?: string;
  /** Current state callback for persistence (e.g., Saved Filters metadata) */
  onStateChange?: (state: VarianceFilterState) => void;
  /** Apply state restored from a saved filter */
  appliedState?: Partial<VarianceFilterState> | null;
  /** Increment to reset variance controls to defaults without remounting */
  resetSignal?: number;
}

const TAB_CONFIG: { key: VarianceTab; label: string }[] = [
  { key: "noChange", label: "Record with No Changes" },
  { key: "withChange", label: "Record with Changes" },
  { key: "all", label: "All" },
];

const VarianceFilter: React.FC<VarianceFilterProps> = ({
  rows,
  numericField,
  onFilteredRows,
  actions,
  columnLabel,
  onStateChange,
  appliedState,
  resetSignal,
}) => {
  const [activeTab, setActiveTab] = useState<VarianceTab>("withChange");
  const [inverted, setInverted] = useState(true);

  /**
   * When restoring a saved filter the appliedState effect sets a new activeTab.
   * That triggers the tab-switch effect in the NEXT render, which would blindly
   * overwrite the saved inverted/sliderRange values.  We use this ref to let
   * the appliedState effect "claim" the next tab-switch run so the tab-switch
   * effect skips its default reset and just clears the flag instead.
   */
  const skipNextTabSwitchResetRef = useRef(false);

  // Compute dynamic min/max from data
  const { dataMin, dataMax } = useMemo(() => {
    const nums = rows
      .map((r) => Number(r[numericField]))
      .filter((n) => !isNaN(n));
    if (nums.length === 0) return { dataMin: 0, dataMax: 0 };
    return {
      dataMin: Math.min(...nums),
      dataMax: Math.max(...nums),
    };
  }, [rows, numericField]);

  const defaultMin = Math.max(dataMin, -30);
  const defaultMax = Math.min(dataMax, 30);

  const [sliderRange, setSliderRange] = useState<[number, number]>([
    defaultMin,
    defaultMax,
  ]);
  const [minInput, setMinInput] = useState<string>(String(defaultMin));
  const [maxInput, setMaxInput] = useState<string>(String(defaultMax));

  // Reset slider range when data bounds change
  useEffect(() => {
    setSliderRange([Math.max(dataMin, -30), Math.min(dataMax, 30)]);
    setMinInput(String(Math.max(dataMin, -30)));
    setMaxInput(String(Math.min(dataMax, 30)));
  }, [dataMin, dataMax]);

  // Reset slider and inversion when switching tabs.
  // Skipped when a saved filter is being restored (skipNextTabSwitchResetRef)
  // so the saved inverted/sliderRange are not overwritten.
  useEffect(() => {
    if (skipNextTabSwitchResetRef.current) {
      skipNextTabSwitchResetRef.current = false;
      return;
    }
    if (activeTab === "all") {
      // In 'All' mode, show full data range and no inversion
      setSliderRange([dataMin, dataMax]);
      setMinInput(String(dataMin));
      setMaxInput(String(dataMax));
      setInverted(false);
    } else {
      setSliderRange([Math.max(dataMin, -30), Math.min(dataMax, 30)]);
      setMinInput(String(Math.max(dataMin, -30)));
      setMaxInput(String(Math.min(dataMax, 30)));
      setInverted(true);
    }
  }, [activeTab, dataMin, dataMax]);

  // Apply filter whenever tab, slider, or inversion changes
  const applyFilter = useCallback(
    (tab: VarianceTab, range: [number, number], isInverted: boolean) => {
      const shouldApplyRange = dataMin !== dataMax;
      const inRange = (val: number) => val >= range[0] && val <= range[1];
      const matchesRange = (val: number) =>
        shouldApplyRange ? (isInverted ? !inRange(val) : inRange(val)) : true;
      const isNoChangeValue = (val: number) => isNaN(val) || val === 0;

      let filtered: any[];
      switch (tab) {
        case "noChange":
          filtered = rows.filter((r) => {
            const val = Number(r[numericField]);
            // "No change" includes explicit zero and NA-like values.
            return isNoChangeValue(val);
          });
          break;
        case "withChange":
          filtered = rows.filter((r) => {
            const val = Number(r[numericField]);
            return !isNaN(val) && val !== 0 && matchesRange(val);
          });
          break;
        case "all":
        default:
          filtered = rows.filter((r) => {
            const val = Number(r[numericField]);
            // Include row if value is numeric and matches range, or if value is non-numeric (e.g., 'N/A')
            return (!isNaN(val) && matchesRange(val)) || isNaN(val);
          });
          break;
      }
      onFilteredRows(filtered);
    },
    [rows, numericField, onFilteredRows, dataMin, dataMax],
  );

  useEffect(() => {
    applyFilter(activeTab, sliderRange, inverted);
  }, [activeTab, sliderRange, inverted, applyFilter]);

  useEffect(() => {
    onStateChange?.({
      activeTab,
      inverted,
      sliderRange,
    });
  }, [activeTab, inverted, sliderRange, onStateChange]);

  useEffect(() => {
    if (!appliedState) return;

    const nextTab =
      appliedState.activeTab ?? activeTab;
    const nextInverted =
      appliedState.inverted ?? inverted;

    const nextRangeRaw =
      appliedState.sliderRange ?? sliderRange;
    const nextMin = Math.max(
      dataMin,
      Math.min(nextRangeRaw[0], dataMax),
    );
    const nextMax = Math.max(
      nextMin,
      Math.min(nextRangeRaw[1], dataMax),
    );

    // If the tab is about to change, the tab-switch effect will fire in the
    // next render.  Signal it to skip its default reset so the saved inverted
    // value and slider range are preserved.
    if (nextTab !== activeTab) {
      skipNextTabSwitchResetRef.current = true;
    }

    setActiveTab(nextTab);
    setInverted(nextInverted);
    setSliderRange([nextMin, nextMax]);
    setMinInput(String(nextMin));
    setMaxInput(String(nextMax));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    appliedState,
    dataMin,
    dataMax,
  ]);

  useEffect(() => {
    if (resetSignal === undefined) return;
    skipNextTabSwitchResetRef.current = false;
    setActiveTab("withChange");
    setInverted(true);
    setSliderRange([defaultMin, defaultMax]);
    setMinInput(String(defaultMin));
    setMaxInput(String(defaultMax));
  }, [resetSignal, defaultMin, defaultMax]);

  const handleSliderChange = (_: Event, value: number | number[]) => {
    const v = value as number[];
    setSliderRange([v[0], v[1]]);
    setMinInput(String(v[0]));
    setMaxInput(String(v[1]));
  };

  const handleMinInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    setMinInput(raw);
  };

  const handleMaxInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    setMaxInput(raw);
  };

  const handleMinBlur = () => {
    const parsed = Number(minInput);
    if (!isNaN(parsed)) {
      const clamped = Math.max(dataMin, Math.min(parsed, sliderRange[1]));
      setSliderRange([clamped, sliderRange[1]]);
      setMinInput(String(clamped));
    } else {
      setMinInput(String(sliderRange[0]));
    }
  };

  const handleMaxBlur = () => {
    const parsed = Number(maxInput);
    if (!isNaN(parsed)) {
      const clamped = Math.min(dataMax, Math.max(parsed, sliderRange[0]));
      setSliderRange([sliderRange[0], clamped]);
      setMaxInput(String(clamped));
    } else {
      setMaxInput(String(sliderRange[1]));
    }
  };

  const showSlider = activeTab === "withChange" || activeTab === "all";

  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      sx={{ width: "100%", minHeight: 40, height: 40 }}
    >
      {/* Left side: segmented toggle tabs */}
      <Stack direction="row" gap={1} alignItems="center" sx={{ flexShrink: 0 }}>
        <ToggleButtonGroup
          value={activeTab}
          exclusive
          onChange={(_, val) => {
            if (val !== null) setActiveTab(val);
          }}
          size="small"
          sx={{
            height: 30,
            borderRadius: 28,
            overflow: "hidden",
            border: "1px solid rgba(0,0,0,0.23)",
            "& .MuiToggleButtonGroup-grouped": {
              border: 0,
              borderRadius: "0 !important",
              px: 1.5,
              py: 0.25,
              fontSize: 12,
              fontWeight: 500,
              textTransform: "none",
              color: "#000",
              "&.Mui-selected": {
                backgroundColor: "#000",
                color: "#fff",
                "&:hover": { backgroundColor: "#333" },
              },
              "&:not(:first-of-type)": {
                borderLeft: "1px solid rgba(0,0,0,0.12)",
              },
            },
          }}
        >
          {TAB_CONFIG.map(({ key, label }) => (
            <ToggleButton key={key} value={key} disableRipple>
              {label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Stack>

      {/* Center: column label + slider (conditionally) */}
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          display: "flex",
          justifyContent: "flex-start",
          alignItems: "center",
          height: "100%",
          ml: 2,
        }}
      >
        {showSlider && dataMin !== dataMax && (
          <Stack
            direction="row"
            alignItems="center"
            gap={1}
            sx={{ height: "100%" }}
          >
            <TextField
              size="small"
              value={minInput}
              onChange={handleMinInputChange}
              onBlur={handleMinBlur}
              inputProps={{ "aria-label": "Min variance" }}
              sx={{
                width: 60,
                "& input": { textAlign: "center", py: "3px", fontSize: 12 },
                "& .MuiOutlinedInput-root": { height: 26 },
              }}
            />
            <Box
              sx={{
                position: "relative",
                width: 180,
                display: "flex",
                alignItems: "center",
              }}
            >
              {columnLabel && (
                <Typography
                  variant="caption"
                  sx={{
                    position: "absolute",
                    top: -12,
                    left: "50%",
                    transform: "translateX(-50%)",
                    fontWeight: 600,
                    fontSize: 10,
                    color: "#555",
                    whiteSpace: "nowrap",
                    lineHeight: 1,
                  }}
                >
                  {columnLabel}
                </Typography>
              )}
              <Slider
                value={sliderRange}
                onChange={handleSliderChange}
                min={dataMin}
                max={dataMax}
                valueLabelDisplay="auto"
                disableSwap
                sx={{
                  width: "100%",
                  p: 0,
                  color: "#000",
                  "& .MuiSlider-thumb": { width: 10, height: 10 },
                  "& .MuiSlider-rail": { height: 2 },
                  "& .MuiSlider-track": { height: 2 },
                }}
              />
            </Box>
            <TextField
              size="small"
              value={maxInput}
              onChange={handleMaxInputChange}
              onBlur={handleMaxBlur}
              inputProps={{ "aria-label": "Max variance" }}
              sx={{
                width: 60,
                "& input": { textAlign: "center", py: "3px", fontSize: 12 },
                "& .MuiOutlinedInput-root": { height: 26 },
              }}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={inverted}
                  onChange={(e) => setInverted(e.target.checked)}
                  size="small"
                  sx={{
                    color: "#000",
                    "&.Mui-checked": { color: "#000" },
                    p: 0.25,
                  }}
                />
              }
              label="Inversion"
              sx={{
                ml: 0.5,
                mr: 0,
                "& .MuiFormControlLabel-label": {
                  fontSize: 11,
                  whiteSpace: "nowrap",
                },
              }}
            />
          </Stack>
        )}
      </Box>

      {/* Right side: action buttons */}
      <Stack direction="row" gap={1} alignItems="center" sx={{ flexShrink: 0 }}>
        {actions}
      </Stack>
    </Stack>
  );
};

export default VarianceFilter;
